import React, { useCallback, useEffect, useState } from "react";
import { Receipt, Loader2, Download, Undo2, Search, Trash2, TrendingUp, X } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api from "../../api";
import { useToast } from "../../hooks/use-toast";

const RANGES = [["today", "Today"], ["7d", "7 days"], ["30d", "30 days"], ["90d", "90 days"], ["all", "All time"]];
const TYPES = [["all", "Everything"], ["subscription", "Plans"], ["boost", "Boosts"], ["feature", "Featured"], ["addon", "Add-ons"]];
const STATUSES = [["all", "All"], ["paid", "Paid"], ["refunded", "Refunded"], ["pending", "Unfinished"]];

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const REFUND_PRESETS = ["Duplicate charge", "Goodwill gesture", "Billing error", "Customer request", "Test transaction"];

// One ledger for the money: what came in, what went back, and a refund that also switches off
// whatever the payment bought.
export const AdminAccounting = () => {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [range, setRange] = useState("30d");
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");
  const [dialog, setDialog] = useState(null); // { row, manual }
  const [reason, setReason] = useState("");

  const params = useCallback(() => {
    const p = new URLSearchParams({ range });
    if (type !== "all") p.set("type", type);
    if (status !== "all") p.set("status", status);
    if (q.trim()) p.set("q", q.trim());
    return p;
  }, [range, type, status, q]);

  const load = useCallback(() => {
    api.get(`/admin/accounting?${params().toString()}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [params]);
  useEffect(load, [load]);

  const refund = (row, manual = false) => { setDialog({ row, manual }); setReason(""); };

  const confirmRefund = async () => {
    if (!dialog) return;
    const { row, manual } = dialog;
    setBusy(row.id);
    try {
      await api.post(`/admin/accounting/${row.id}/refund`, { reason: reason.trim(), manual });
      toast({ title: `Refunded ${money(row.amount)}`, description: `${row.shop} has been told and it's switched off.` });
      setDialog(null);
      load();
    } catch (e) { toast({ title: "Refund failed", description: e?.response?.data?.detail || "Please try again." }); }
    finally { setBusy(""); }
  };

  const exportCsv = async () => {
    setBusy("csv");
    try {
      const res = await api.get(`/admin/accounting/export?${params().toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `lytepress-sales-${range}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast({ title: "Could not build the file" }); }
    finally { setBusy(""); }
  };

  if (!data) return null;
  const T = data.totals;

  // Books get read by month and by year, so offer both without a date picker nobody wants.
  const now = new Date();
  const periods = {
    years: Array.from({ length: 3 }, (_, i) => String(now.getFullYear() - i)),
    months: Array.from({ length: 12 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    }),
  };

  const clearBooks = async () => {
    const what = range === "all" ? "every payment record" : "the payments in this view";
    if (!window.confirm(`Delete ${what}? This can't be undone — it's for clearing test data, not real sales.`)) return;
    setBusy("clear");
    try {
      const { data: res } = await api.post("/admin/accounting/clear", { confirm: "CLEAR", range });
      toast({ title: `Cleared ${res.removed} record(s)` });
      load();
    } catch (e) { toast({ title: e?.response?.data?.detail || "Could not clear that" }); }
    finally { setBusy(""); }
  };

  const Chips = ({ options, value, set, prefix }) => (
    <div className="flex items-center gap-1 bg-secondary rounded-full p-1 overflow-x-auto no-scrollbar max-w-full">
      {options.map(([v, label]) => (
        <button key={v} onClick={() => set(v)} data-testid={`${prefix}-${v}`}
          className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-bold transition-all ${value === v ? "bg-card shadow text-foreground" : "text-muted-foreground"}`}>{label}</button>
      ))}
    </div>
  );

  return (
    <div className="bg-card border border-border rounded-2xl p-5 space-y-4" data-testid="admin-accounting">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-bold text-foreground flex items-center gap-2"><Receipt size={18} className="text-[#16a34a]" /> Accounting</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Sales, discounts and refunds. Refunding also switches off what the payment bought. Use
            “Record” when you've already refunded someone in Stripe.
          </p>
        </div>
        <button onClick={exportCsv} disabled={busy === "csv"} data-testid="accounting-export"
          className="shrink-0 inline-flex items-center gap-1.5 text-xs font-bold border border-border hover:border-[#16a34a] rounded-xl px-3 py-2 transition-colors">
          {busy === "csv" ? <Loader2 className="animate-spin" size={13} /> : <Download size={13} />}
          <span className="hidden sm:inline">Export</span> CSV
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        {[["Net revenue", T.net, "text-[#16a34a]", "net", "all"], ["Taken", T.gross, "text-foreground", "gross", "paid"],
          ["Refunded", T.refunded, "text-rose-600", "refunded", "refunded"], ["Given in discounts", T.discounts, "text-amber-600", "discounts", "paid"]].map(([label, val, cls, key, filt]) => (
          <button key={key} type="button" onClick={() => setStatus(filt)} data-testid={`accounting-total-${key}`}
            className={`text-left border rounded-xl p-3 transition-all hover:shadow-md active:scale-[0.98] ${status === filt && filt !== "all" ? "border-[#16a34a] ring-1 ring-[#16a34a]/30" : "border-border hover:border-[#16a34a]/50"}`}>
            <p className={`text-xl font-extrabold leading-none ${cls}`}>{money(val)}</p>
            <p className="text-xs font-semibold text-foreground mt-1">{label}</p>
          </button>
        ))}
      </div>

      <div className="border border-border rounded-xl p-3 sm:p-4" data-testid="accounting-chart">
        <div className="flex items-center gap-2 mb-2">
          <TrendingUp size={15} className="text-[#16a34a]" />
          <p className="text-xs font-bold text-foreground">Sales over time</p>
          <span className="text-[11px] text-muted-foreground">net · {RANGES.find(([v]) => v === range)?.[1] || "this view"}</span>
        </div>
        {(data.series || []).some((d) => d.gross > 0) ? (
          <div className="h-52 w-full" data-testid="accounting-chart-canvas">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.series} margin={{ top: 5, right: 10, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="acctNet" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#16a34a" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#16a34a" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="var(--muted-foreground)" interval="preserveStartEnd" minTickGap={20} />
                <YAxis tick={{ fontSize: 11 }} stroke="var(--muted-foreground)" width={44} tickFormatter={(v) => `$${v}`} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid var(--border)", background: "var(--card)", fontSize: 12 }}
                  formatter={(v, n) => [money(v), n === "net" ? "Net" : n === "gross" ? "Taken" : "Refunded"]} />
                <Area type="monotone" dataKey="net" name="net" stroke="#16a34a" strokeWidth={2} fill="url(#acctNet)" />
                <Area type="monotone" dataKey="refunded" name="refunded" stroke="#e11d48" strokeWidth={1.5} fill="transparent" strokeDasharray="4 3" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground py-6 text-center" data-testid="accounting-chart-empty">
            No paid sales in this period yet — the line will fill in as real payments come through.
          </p>
        )}
      </div>

      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2">
        <Chips options={RANGES} value={range} set={setRange} prefix="accounting-range" />
        <select value={range.startsWith("month:") || range.startsWith("year:") ? range : ""}
          onChange={(e) => e.target.value && setRange(e.target.value)} data-testid="accounting-period"
          className="bg-secondary rounded-xl px-3 py-2 text-xs font-semibold text-foreground outline-none">
          <option value="">By month or year…</option>
          {periods.years.map((y) => <option key={y} value={`year:${y}`}>{y}</option>)}
          {periods.months.map((m) => <option key={m} value={`month:${m}`}>{m}</option>)}
        </select>
        <Chips options={TYPES} value={type} set={setType} prefix="accounting-type" />
        <Chips options={STATUSES} value={status} set={setStatus} prefix="accounting-status" />
        <div className="flex items-center gap-2 bg-secondary rounded-xl px-3 py-2 sm:min-w-[200px]">
          <Search size={14} className="text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Shop, email, package or receipt code (LP-…)"
            data-testid="accounting-search" className="bg-transparent outline-none text-xs flex-1 text-foreground" />
        </div>
        <span className="text-xs text-muted-foreground" data-testid="accounting-counts">
          {T.paidCount} paid · {T.refundCount} refunded · {T.pendingCount} unfinished
        </span>
        <button onClick={clearBooks} data-testid="accounting-clear-btn"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-rose-600 border border-rose-500/40 hover:bg-rose-500/10 rounded-xl px-3 py-2 transition-colors">
          <Trash2 size={13} /> Clear {range === "all" ? "everything" : "this view"}
        </button>
      </div>

      {data.rows.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="accounting-empty">No payments in this view.</p>
      ) : (
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm" data-testid="accounting-table">
            <thead>
              <tr className="text-left text-xs text-muted-foreground border-b border-border">
                <th className="py-2 pr-3 font-semibold">Date</th>
                <th className="py-2 pr-3 font-semibold">Shop</th>
                <th className="py-2 pr-3 font-semibold">Bought</th>
                <th className="py-2 pr-3 font-semibold text-right">Amount</th>
                <th className="py-2 pr-3 font-semibold">Status</th>
                <th className="py-2 font-semibold"></th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.id} className="border-b border-border/60" data-testid={`accounting-row-${r.id}`}>
                  <td className="py-2.5 pr-3 text-xs text-muted-foreground whitespace-nowrap">{new Date(r.createdAt).toLocaleDateString()}</td>
                  <td className="py-2.5 pr-3">
                    <p className="font-semibold text-foreground">{r.shop}</p>
                    <p className="text-[11px] text-muted-foreground">{r.email || "—"}</p>
                  </td>
                  <td className="py-2.5 pr-3 text-xs text-foreground">
                    {r.packageId}
                    {r.code && <span className="ml-1.5 text-[11px] font-bold text-amber-600">{r.code}</span>}
                    {r.receiptCode && (
                      <span className="block text-[11px] font-mono text-muted-foreground mt-0.5" data-testid={`accounting-receipt-${r.id}`}>
                        {r.receiptCode}{r.invoiceNumber ? ` · ${r.invoiceNumber}` : ""}
                      </span>
                    )}
                  </td>
                  <td className="py-2.5 pr-3 text-right font-bold text-foreground whitespace-nowrap">
                    {money(r.amount)}
                    {r.listAmount > r.amount && <span className="block text-[11px] font-semibold text-muted-foreground line-through">{money(r.listAmount)}</span>}
                  </td>
                  <td className="py-2.5 pr-3">
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${r.status === "paid" ? "bg-[#e7f6ec] text-[#0e6b3e]" : r.status === "refunded" ? "bg-rose-50 text-rose-600 dark:bg-rose-950/40" : "bg-secondary text-muted-foreground"}`}>
                      {r.status === "pending" ? "unfinished" : r.status}
                    </span>
                    {r.refundReason && <p className="text-[11px] text-muted-foreground mt-0.5">{r.refundReason}</p>}
                  </td>
                  <td className="py-2.5 text-right">
                    {r.refundable && (
                      <span className="inline-flex items-center gap-1 whitespace-nowrap">
                        <button onClick={() => refund(r)} disabled={busy === r.id} data-testid={`accounting-refund-${r.id}`}
                          className="inline-flex items-center gap-1.5 text-xs font-bold text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded-lg px-2.5 py-1.5 transition-colors">
                          {busy === r.id ? <Loader2 className="animate-spin" size={13} /> : <Undo2 size={13} />} Refund
                        </button>
                        <button onClick={() => refund(r, true)} disabled={busy === r.id} data-testid={`accounting-record-${r.id}`}
                          title="Already refunded in Stripe — just update the books"
                          className="text-[11px] font-semibold text-muted-foreground hover:text-foreground rounded-lg px-2 py-1.5 transition-colors">
                          Record
                        </button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data.rows.length > 0 && (
        <div className="md:hidden space-y-2" data-testid="accounting-cards">
          {data.rows.map((r) => (
            <div key={r.id} className="border border-border rounded-xl p-3" data-testid={`accounting-card-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-bold text-foreground text-sm truncate">{r.shop}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{r.email || "—"}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-extrabold text-foreground">{money(r.amount)}</p>
                  {r.listAmount > r.amount && <p className="text-[11px] text-muted-foreground line-through">{money(r.listAmount)}</p>}
                </div>
              </div>
              <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px]">
                <span className="text-muted-foreground">{new Date(r.createdAt).toLocaleDateString()}</span>
                <span className="font-semibold text-foreground">{r.packageId}</span>
                {r.code && <span className="font-bold text-amber-600">{r.code}</span>}
                {r.receiptCode && <span className="font-mono text-muted-foreground" data-testid={`accounting-receipt-card-${r.id}`}>{r.receiptCode}</span>}
                <span className={`font-bold px-2 py-0.5 rounded-full ${r.status === "paid" ? "bg-[#e7f6ec] text-[#0e6b3e]" : r.status === "refunded" ? "bg-rose-50 text-rose-600 dark:bg-rose-950/40" : "bg-secondary text-muted-foreground"}`}>
                  {r.status === "pending" ? "unfinished" : r.status}
                </span>
              </div>
              {r.refundReason && <p className="text-[11px] text-muted-foreground mt-1">{r.refundReason}</p>}
              {r.refundable && (
                <div className="mt-2 flex gap-2">
                  <button onClick={() => refund(r)} disabled={busy === r.id} data-testid={`accounting-refund-m-${r.id}`}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-bold text-rose-600 border border-rose-200 dark:border-rose-900 rounded-lg py-2 transition-colors">
                    {busy === r.id ? <Loader2 className="animate-spin" size={13} /> : <Undo2 size={13} />} Refund
                  </button>
                  <button onClick={() => refund(r, true)} disabled={busy === r.id} data-testid={`accounting-record-m-${r.id}`}
                    className="inline-flex items-center justify-center text-xs font-semibold text-muted-foreground border border-border rounded-lg px-3 py-2 transition-colors">
                    Record
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {dialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="refund-dialog"
          onClick={(e) => { if (e.target === e.currentTarget && busy !== dialog.row.id) setDialog(null); }}>
          <div className="w-full max-w-md bg-card border border-border rounded-2xl p-5 space-y-4 shadow-xl">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-bold text-foreground">
                  {dialog.manual ? "Record a refund" : "Refund"} · {money(dialog.row.amount)}
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {dialog.row.shop}{dialog.row.receiptCode ? ` · ${dialog.row.receiptCode}` : ""}
                </p>
              </div>
              <button onClick={() => busy !== dialog.row.id && setDialog(null)} data-testid="refund-dialog-close"
                className="text-muted-foreground hover:text-foreground rounded-lg p-1 transition-colors"><X size={18} /></button>
            </div>

            <p className="text-xs text-muted-foreground">
              {dialog.manual
                ? "You've already refunded them in Stripe — this only updates your books and switches off what they bought."
                : "The money goes back to their card and what it paid for is switched off."}
            </p>

            <div>
              <p className="text-xs font-semibold text-foreground mb-1.5">Reason (they'll see it)</p>
              <div className="flex flex-wrap gap-1.5">
                {REFUND_PRESETS.map((p) => (
                  <button key={p} onClick={() => setReason(p)} data-testid={`refund-preset-${p.split(" ")[0].toLowerCase()}`}
                    className={`text-xs font-semibold rounded-full px-3 py-1.5 border transition-colors ${reason === p ? "bg-[#16a34a] text-white border-[#16a34a]" : "border-border text-muted-foreground hover:border-[#16a34a]/50"}`}>
                    {p}
                  </button>
                ))}
              </div>
              <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="refund-reason-input"
                placeholder="Or type a custom reason…"
                className="mt-2 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground" />
            </div>

            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setDialog(null)} disabled={busy === dialog.row.id} data-testid="refund-cancel"
                className="text-sm font-semibold text-muted-foreground hover:text-foreground rounded-xl px-4 py-2 transition-colors">Cancel</button>
              <button onClick={confirmRefund} disabled={busy === dialog.row.id} data-testid="refund-confirm"
                className="inline-flex items-center gap-1.5 text-sm font-bold text-white bg-rose-600 hover:bg-rose-700 rounded-xl px-4 py-2 transition-colors disabled:opacity-60">
                {busy === dialog.row.id ? <Loader2 className="animate-spin" size={14} /> : <Undo2 size={14} />}
                {dialog.manual ? "Record refund" : `Refund ${money(dialog.row.amount)}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAccounting;
